// Where a user came from before signing up, read from the landing page's
// `utm_source` on the first app load and kept in this browser. Only known
// partner sources are stored, so a crafted link cannot switch on arbitrary UI.
//
// The SAP Ariba landing page links to
// app.turnus.ai?signup=true&utm_source=ariba&..., so a supplier who signs up
// from there sees the Ariba checklist on the home screen.

export type ReferralSource = "ariba"

const KNOWN_SOURCES: readonly ReferralSource[] = ["ariba"]

const REFERRAL_KEY = "turnus.referralSource"
const ARIBA_DISMISSED_KEY = "turnus.aribaOnboarding.dismissed"
const FIREFOX_CONFIRMED_KEY = "turnus.aribaOnboarding.firefoxExtensionConfirmed"
const EXTENSION_HANDOFF_KEY = "turnus.aribaOnboarding.extensionHandoffDone"

const isKnownSource = (value: string | null): value is ReferralSource =>
  !!value && (KNOWN_SOURCES as readonly string[]).includes(value)

// localStorage can throw (private mode, blocked site data); the checklist is a
// convenience, so a failed read or write only means it does not show.
const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignored, see readStorage
  }
}

/** Stores a known `utm_source` from the current URL. Called once at app start. */
export const captureReferralSource = (search = window.location.search) => {
  const source = new URLSearchParams(search).get("utm_source")?.toLowerCase()
  if (source && isKnownSource(source)) {
    writeStorage(REFERRAL_KEY, source)
  }
}

export const getReferralSource = (): ReferralSource | null => {
  const source = readStorage(REFERRAL_KEY)
  return isKnownSource(source) ? source : null
}

/**
 * Adds the stored referral source to a Cognito SignUp or ConfirmSignUp call as
 * clientMetadata. The post-confirmation trigger keeps a known source on the
 * account, and the backend gives an Ariba trial a higher extension limit.
 */
export const withReferralSource = <
  T extends { options?: { clientMetadata?: Record<string, string> } },
>(
  input: T,
): T => {
  const source = getReferralSource()
  if (!source) return input
  return {
    ...input,
    options: {
      ...input.options,
      clientMetadata: {
        ...input.options?.clientMetadata,
        referral_source: source,
      },
    },
  }
}

export const isAribaOnboardingDismissed = () =>
  readStorage(ARIBA_DISMISSED_KEY) === "true"

export const dismissAribaOnboarding = () =>
  writeStorage(ARIBA_DISMISSED_KEY, "true")

// Firefox pages cannot ask the extension whether it is installed, so the user
// confirms it once.
export const isFirefoxExtensionConfirmed = () =>
  readStorage(FIREFOX_CONFIRMED_KEY) === "true"

export const confirmFirefoxExtension = () =>
  writeStorage(FIREFOX_CONFIRMED_KEY, "true")

// The checklist hands the session to the extension once per browser, so a
// later sign-out in the extension sticks.
export const isExtensionHandoffDone = () =>
  readStorage(EXTENSION_HANDOFF_KEY) === "true"

export const markExtensionHandoffDone = () =>
  writeStorage(EXTENSION_HANDOFF_KEY, "true")

// The step states the checklist last saw in this tab. Kept for the tab, not
// the component: leaving Home to upload documents and coming back still counts
// the step as completed, and a reload does not count it twice.
const STEP_BASELINE_KEY = "turnus.aribaOnboarding.stepBaseline"

export const readStepBaseline = (): Record<string, boolean> | null => {
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(STEP_BASELINE_KEY) ?? "null",
    )
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, boolean>)
      : null
  } catch {
    return null
  }
}

export const writeStepBaseline = (steps: Record<string, boolean>) => {
  try {
    sessionStorage.setItem(STEP_BASELINE_KEY, JSON.stringify(steps))
  } catch {
    // ignored, see readStorage
  }
}
